import {
  type ColumnDef,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  type Row,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { DeltaCell } from "@/components/ci/overview-cells";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cryodDimensionLabel, cryodValueLabel } from "@/lib/ci-filters";
import type { CryodOverviewRow } from "@/lib/types";
import { cn, computeDelta, formatMetricValue } from "@/lib/utils";

const SPARK_IMPROVE = "#34d399"; // emerald — metric trending down (better)
const SPARK_REGRESS = "#fb7185"; // rose — metric trending up (worse)
const SPARK_FLAT = "#a1a1a6"; // muted

const SKELETON_ROW_KEYS = ["sk0", "sk1", "sk2", "sk3", "sk4", "sk5", "sk6", "sk7"];

export type DrillPin = { dimension: string; value: string };

function PassRateCell({ rate }: { rate: number | null }) {
  if (rate == null) return <span className="font-mono text-xs text-muted-foreground/50">-</span>;
  const percent = rate * 100;
  const tone =
    rate >= 0.95
      ? { bar: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" }
      : rate >= 0.8
        ? { bar: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" }
        : { bar: "bg-rose-500", text: "text-rose-600 dark:text-rose-400" };
  return (
    <div className="flex flex-col gap-1">
      <span className={cn("font-mono text-xs font-medium tabular-nums", tone.text)}>
        {percent.toFixed(percent < 100 ? 1 : 0)}%
      </span>
      <div className="h-1 w-16 overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full", tone.bar)} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function Sparkline({ points }: { points: CryodOverviewRow["sparkline"] }) {
  const data = points.filter((point) => point.value != null);
  if (data.length < 2) return <span className="text-xs text-muted-foreground/40">—</span>;
  const first = data[0]?.value ?? 0;
  const last = data[data.length - 1]?.value ?? 0;
  const stroke = last < first ? SPARK_IMPROVE : last > first ? SPARK_REGRESS : SPARK_FLAT;
  return (
    <div className="h-7 w-28">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
          <Line
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function GroupCell({ dimension, value }: { dimension: string; value: string }) {
  const label = cryodValueLabel(dimension, value);
  if (!value) return <span className="text-sm text-muted-foreground/60 italic">unknown</span>;
  if (dimension === "storage_engine_enabled" || dimension.startsWith("extra:")) {
    const variant =
      dimension === "storage_engine_enabled" && value === "1"
        ? "default"
        : dimension === "storage_engine_enabled"
          ? "outline"
          : "secondary";
    return (
      <Badge variant={variant} className="font-mono">
        {label}
      </Badge>
    );
  }
  if (dimension === "model_id" || dimension === "test_name") {
    return <span className="text-sm font-medium">{label}</span>;
  }
  return <span className="font-mono text-sm">{label}</span>;
}

// The pins (dimension+value filters) that identify a clicked row, including its
// parent group for sub-rows — used to scope a drill-down.
function rowPins(row: Row<CryodOverviewRow>, groupBy: string, subGroupBy: string): DrillPin[] {
  if (row.depth === 0) {
    return [{ dimension: groupBy, value: row.original.group_key }];
  }
  const parent = row.getParentRow();
  return [
    ...(parent ? [{ dimension: groupBy, value: parent.original.group_key }] : []),
    { dimension: subGroupBy, value: row.original.group_key },
  ];
}

/**
 * The cryod pivot/breakdown table — a pure renderer (rows in, table out). Used by
 * the overview page and the drill-down detail's nested re-pivot. `onDrill` makes
 * rows clickable; it receives the filter pins identifying the clicked slice.
 */
export function CryodPivotTable({
  rows,
  isLoading,
  groupBy,
  subGroupBy,
  metricLabel,
  metricUnit,
  onDrill,
  expandedKeys,
  onToggleRow,
}: {
  rows: CryodOverviewRow[];
  isLoading: boolean;
  groupBy: string;
  subGroupBy: string;
  metricLabel: string;
  metricUnit: string;
  onDrill?: (pins: DrillPin[]) => void;
  // Optional controlled expansion by stable group key (the overview persists it
  // in the URL). When omitted, the table manages expansion internally.
  expandedKeys?: string[];
  onToggleRow?: (rowId: string) => void;
}) {
  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({});
  const controlled = expandedKeys !== undefined;
  const expanded: ExpandedState = controlled
    ? Object.fromEntries(expandedKeys.map((key) => [key, true]))
    : internalExpanded;

  const columns = useMemo<ColumnDef<CryodOverviewRow>[]>(
    () => [
      {
        id: "group",
        header: cryodDimensionLabel(groupBy),
        cell: ({ row }) => {
          const dimension = row.depth === 0 ? groupBy : subGroupBy;
          return (
            <div
              className="flex items-center gap-1.5"
              style={{ paddingLeft: `${row.depth * 1.25}rem` }}
            >
              {row.getCanExpand() ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (controlled) onToggleRow?.(row.id);
                    else row.toggleExpanded();
                  }}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {row.getIsExpanded() ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                </button>
              ) : (
                <span className="w-3.5" />
              )}
              <GroupCell dimension={dimension} value={row.original.group_key} />
            </div>
          );
        },
      },
      {
        id: "runs",
        header: () => <div className="text-right">Runs</div>,
        cell: ({ row }) => (
          <div className="text-right font-mono text-sm tabular-nums text-muted-foreground">
            {row.original.run_count}
          </div>
        ),
      },
      {
        id: "pass_rate",
        header: "Pass %",
        cell: ({ row }) => <PassRateCell rate={row.original.pass_rate} />,
      },
      {
        id: "metric",
        header: () => <div className="text-right">{metricLabel}</div>,
        cell: ({ row }) => (
          <div className="text-right font-mono text-sm font-medium tabular-nums">
            {formatMetricValue(row.original.value, metricUnit)}
          </div>
        ),
      },
      {
        id: "delta",
        header: () => (
          <div className="text-right" title="Recent half of the window vs the earlier half">
            Δ
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DeltaCell delta={computeDelta(row.original.value, row.original.prev_value)} />
          </div>
        ),
      },
      {
        id: "trend",
        header: "Trend",
        cell: ({ row }) => (row.depth === 0 ? <Sparkline points={row.original.sparkline} /> : null),
      },
    ],
    [groupBy, subGroupBy, metricLabel, metricUnit, controlled, onToggleRow],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { expanded },
    onExpandedChange: setInternalExpanded,
    // Stable row id (group key, not index) so persisted/expanded state survives
    // search-filtering and is shareable via the URL.
    getRowId: (row, _index, parent) => (parent ? `${parent.id}>${row.group_key}` : row.group_key),
    getSubRows: (row) => row.sub_rows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            SKELETON_ROW_KEYS.map((rowKey) => (
              <TableRow key={rowKey}>
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-32 text-center">
                <p className="text-sm text-muted-foreground">No cryod runs match these filters.</p>
                <p className="mt-1 text-xs text-muted-foreground/60">
                  Runs published before this dashboard shipped lack these dimensions and group as
                  "unknown".
                </p>
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={onDrill ? () => onDrill(rowPins(row, groupBy, subGroupBy)) : undefined}
                className={cn(row.depth > 0 && "bg-muted/20", onDrill && "cursor-pointer")}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
